/**
 * FreshCards — Embedding API v2 Demo (SPA integration smoke test)
 *
 * Renders inside the FreshCards SPA at `/embed-demo` (or via direct mount).
 * Exercises the EmbeddingAPIv2 contract end-to-end so the bundle grep can
 * confirm `mountRuntime` / `EmbeddingAPIv2` / `HostRenderer` are included
 * in dist/assets/*.js (not tree-shaken because nothing imports them).
 *
 * Phase 4 wiring — see /workspace/.home/mavis-kits/PROMPT-F12-PHASE-4.md
 * for the canonical pattern. This is the FreshCards-side reference; real
 * hosts (Vibecoder, Gallery) will ship their own host adapter.
 */

import React, { useEffect, useRef } from 'react';
import { Box, Code, Stack, Text, Title } from '@mantine/core';
import { embedRuntime, mountRuntime } from '../../integration/embedding/EmbeddingAPIv2';
import type { EmbeddingBundleV2, HostRenderer } from '../../integration/embedding/EmbeddingAPIv2';
import { FreshCardsHostAdapter } from '../../integration/embedding/hostAdapter';
import { applyEmbedBundle } from '../../integration/embedding/WhiteLabelEmbeddingAPI';
import { composeHostCallbacks } from '../../integration/embedding/hostCallbacks';

/**
 * A host renderer that paints a marker into the container. This is the
 * host-side implementation of how the bundle gets mounted.
 */
const renderer: HostRenderer = {
  mount(container, bundle) {
    container.dataset.fcDemoMounted = '1';
    container.dataset.fcDemoBundleId = bundle.id;
    container.innerHTML = `<div style="padding:16px;background:rgba(124,92,255,0.08);border:1px dashed rgba(124,92,255,0.3);border-radius:6px;font-family:monospace;font-size:13px;color:#c4b5fd;">
      <strong>EmbeddingAPIv2 mounted</strong> — bundle id: ${bundle.id}<br/>
      <span style="opacity:0.6">schema: ${bundle.schemaVersion} · adapters: ${bundle.adapters.blockRegistry.length} blocks · theme: ${bundle.theme.palette.brand}</span>
    </div>`;
  },
  unmount(container) {
    delete container.dataset.fcDemoMounted;
    delete container.dataset.fcDemoBundleId;
    container.innerHTML = '';
  },
};

export const FreshCardsEmbedDemo: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [log, setLog] = React.useState<string[]>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    // 1. Build a minimal EmbeddingBundleV2.
    const bundle: EmbeddingBundleV2 = embedRuntime({
      theme: {
        palette: { brand: '#7c5cff', accent: '#22d3ee', surface: '#0f172a', text: '#e2e8f0', muted: '#94a3b8' },
        geometry: { radius: '8px', spacing: '12px' },
        typography: { family: 'Inter, system-ui, sans-serif' },
        density: 'comfortable',
      },
      layout: {
        grid: { columns: 12, gutter: 16, maxWidth: 1200 },
        sections: [],
      },
      adapters: {
        blockRegistry: [],
        dataModel: null,
        semantic: null,
        theme: null,
        layout: null,
      },
      mount: { container: '#fc-embed-target', useHostRenderer: true },
      meta: { source: 'fc-embed-demo', createdAt: new Date().toISOString() },
    });

    // 2. Build the host adapter with 3 callbacks (onNavigate, onSurfaceChange, onCommandExecute).
    const adapter = new FreshCardsHostAdapter(composeHostCallbacks({
      onNavigate: (p) => setLog((l) => [...l, `onNavigate → ${p.url}`]),
      onSurfaceChange: (p) => setLog((l) => [...l, `onSurfaceChange → ${p.surface}`]),
      onCommandExecute: (p) => setLog((l) => [...l, `onCommandExecute → ${p.command}`]),
    }));

    // 3. Mount via EmbeddingAPIv2 (uses adapter as HostRenderer).
    const result = mountRuntime(bundle, adapter);

    // 4. Also exercise the v1 WhiteLabelEmbeddingAPI for the deprecation path.
    const v1Result = applyEmbedBundle({
      schema: 1,
      kind: 'account',
      id: 'demo-account',
      host: { origin: window.location.origin, features: {} },
      surface: { surface: 'panel', mode: 'embed' },
      createdAt: new Date().toISOString(),
    });

    setLog((l) => [
      ...l,
      `v2 mountRuntime → ${result.ok ? 'ok' : `error: ${result.error}`}`,
      `v1 applyEmbedBundle → ${v1Result.ok ? 'ok' : `error: ${v1Result.error}`}`,
      `globalThis.__fvs_host registered: ${!!FreshCardsHostAdapter.getGlobal()}`,
    ]);
  }, []);

  return (
    <Box p="xl" style={{ maxWidth: 880, margin: '0 auto' }}>
      <Title order={2} mb="md">Embedding API v2 — Demo</Title>
      <Text c="dimmed" size="sm" mb="lg">
        FreshCards mounted via EmbeddingAPIv2 contract. Real hosts (Vibecoder, Gallery)
        will ship their own HostRenderer implementation; this is the FreshCards-side
        reference. See src/integration/embedding/hostAdapter.ts.
      </Text>

      <Stack gap="md">
        <Box
          id="fc-embed-target"
          ref={containerRef}
          p="md"
          style={{ minHeight: 80, border: '1px solid #2d2a4a', borderRadius: 8 }}
        />

        <Box>
          <Text size="xs" c="dimmed" mb={4}>Callback log (3 callbacks wired):</Text>
          <Code block style={{ fontSize: 11 }}>
            {log.length === 0 ? '(mounting...)' : log.join('\n')}
          </Code>
        </Box>
      </Stack>
    </Box>
  );
};

export default FreshCardsEmbedDemo;
